// Import react libs
import { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';

// Import style
import {
    HeaderWrapper,
    LeftSection,
    CenterSection,
    RightSection,
    Logo,
    LogoutButton,
} from './style';

// Import contexts
import { AuthContext } from '../../utils/contexts/AuthContext';

// Import hooks
import { useLogout } from '../../utils/hooks/auth';

// Import assets
import logo from '../../assets/logos/logo.jpg';

// Import texts
import headerTexts from '../../texts/header';

// Import components
import SearchBar from '../SearchBar';
import LateralMenu from '../LateralMenu';

function Header({ LOGO_WIDTH = 50, ICON_WIDTH = 30 }) {
    // Get the user in a variable
    const { user, isAdmin } = useContext(AuthContext);

    // Get the function to logout
    const { logout } = useLogout();

    // Store in useState the header width and if the logo must be displayed
    const [headerWidth, setheaderWidth] = useState(window.innerWidth);
    const [displayLogo, setDisplayLogo] = useState(true);

    // Store in use states if the search input is open and its width
    const [searchInputWidth, setSearchInputWidth] = useState(200);
    const [isSearchInputOpen, setIsSearchInputOpen] = useState(false);

    // Store in a use state an indicator for the logout button to be displayed
    const [displayLogoutButton, setDisplayLogoutButton] = useState(true);

    // Update the header width each time the window is resized
    useEffect(() => {
        // Create a callback function to update headerWidth and searchInputWidth
        function handleWindowResize() {
            // Set the new header width
            setheaderWidth(window.innerWidth);
        }
        // Link an event listener to the callback function
        window.addEventListener('resize', handleWindowResize);

        return () => window.removeEventListener('resize', handleWindowResize);
    }, []);

    // Use effect to update search input max width when windows resize
    useEffect(() => {
        // Define the new Input width to the min between :
        // 200 and
        // HeaderWidth -
        // paddingBetweenBorderAndIcon (2*15px) -
        // iconWidth * 2 -
        // gapBetweenIconsAndInput (2*10px) -
        const newSearchInputWidth = Math.min(
            200,
            headerWidth -
                15 -
                ICON_WIDTH -
                10 -
                /* SearchInput*/
                10 -
                ICON_WIDTH -
                15,
        );

        // Set Input Width
        setSearchInputWidth(newSearchInputWidth);
    }, [headerWidth, ICON_WIDTH]);

    useEffect(() => {
        // Disable logo display when :
        // rightPaddingBetweenBorderAndIcon (15px)
        // + iconWidth
        // + gapBetweenSearchIconAndInput (10px)
        // + searchInputWidth
        // if the logoutButton is displayed
        //      + gapBetweenLogoutButtonAndInput (10px)
        //      + logoutButtonWidth (112px)
        // is greatter than :
        // HeaderWidth/2
        // - LogoWidth/2
        setDisplayLogo(
            !user
                ? !(
                      isSearchInputOpen &&
                      15 + ICON_WIDTH + 10 + searchInputWidth >=
                          headerWidth / 2 - LOGO_WIDTH / 2
                  )
                : !(
                      isSearchInputOpen &&
                      15 + ICON_WIDTH + 10 + searchInputWidth + 10 + 112 >=
                          headerWidth / 2 - LOGO_WIDTH / 2
                  ),
        );
    }, [
        user,
        isSearchInputOpen,
        ICON_WIDTH,
        searchInputWidth,
        headerWidth,
        LOGO_WIDTH,
    ]);

    useEffect(() => {
        // Disable logout button display when :
        // leftPaddingBetweenBorderAndIcon (15px)
        // + iconWidth
        // + gapBetweenSearchIconAndInput (10px)
        // + searchInputWidth
        // + gapBetweenLogoutButtonAndInput (10px)
        // + logoutButtonWidth (112px)
        // is greatter than :
        // headerWidth
        // - leftPaddingBetweenBorderAndIcon (15px)
        // - iconWidth
        // - gapBetweenMenuIconAndLogoutButton (10px)
        setDisplayLogoutButton(
            user &&
                !(
                    isSearchInputOpen &&
                    15 + ICON_WIDTH + 10 + searchInputWidth + 10 + 112 >=
                        headerWidth - 15 - ICON_WIDTH - 10
                ),
        );
    }, [user, isSearchInputOpen, ICON_WIDTH, searchInputWidth, headerWidth]);

    // Handle click on logout button
    async function handleLogoutButtonClick() {
        await logout();
    }

    return (
        <HeaderWrapper>
            <LeftSection>
                <LateralMenu
                    ICON_WIDTH={ICON_WIDTH}
                    latMenuMaxWidth={headerWidth}
                    isAdminConnected={isAdmin}
                />
            </LeftSection>
            {displayLogo && (
                <CenterSection>
                    <Link to="/">
                        <Logo
                            src={logo}
                            alt={headerTexts.Logo}
                            width={LOGO_WIDTH}
                            loading="lazy"
                        />
                    </Link>
                </CenterSection>
            )}
            <RightSection>
                {displayLogoutButton && (
                    <LogoutButton onClick={() => handleLogoutButtonClick()}>
                        {headerTexts.LogoutButtonText}
                    </LogoutButton>
                )}
                <SearchBar
                    ICON_WIDTH={ICON_WIDTH}
                    searchInputWidth={searchInputWidth}
                    setIsSearchInputOpen={setIsSearchInputOpen}
                />
            </RightSection>
        </HeaderWrapper>
    );
}

export default Header;
